import { Button, Form } from '@boltenergy-be/design-system'
import styles from './SubscribeForm.module.scss'
import { FormProvider, useForm } from 'react-hook-form'
import { useState } from 'react'
import { SubscribeFormInputs } from './types'
import { Email, FirstName, LastName } from 'components/inputs'
import { Trans, useTranslation } from 'next-i18next/pages'
import axios from 'axios'
import { WarmProspectFlow } from 'types/customer'
import { updateInput } from 'store/customer/slice'
import { InputGroupKeys, PersonalDataFieldKeys } from 'store/customer/enums'
import { useGetLanguage } from 'hooks/useGetLanguage'
import { useDispatch } from 'react-redux'
import classNames from 'classnames'
import Link from 'components/link/Link'
import { routes } from 'utils/routes'
import { trackEvent } from 'utils/tracking'

const SubscribeForm = () => {
  // Redux
  const dispatch = useDispatch()

  // i18n
  const { t } = useTranslation(['common', 'validation', 'commonFormFields'])
  const language = useGetLanguage()

  // React Hook Form
  const hookForm = useForm<SubscribeFormInputs>()
  const { handleSubmit } = hookForm

  // Local state
  const [submissionState, setSubmissionState] = useState<'loading' | 'success' | 'failed' | null>(null)

  /**
   * Handles the submission of the subscribe form
   *
   * @param {SubscribeFormInputs} data
   */
  const onSubmit = async (data: SubscribeFormInputs) => {
    setSubmissionState('loading')

    try {
      const { firstName, lastName, email } = data

      // Update the fields in the registration state (firstname, lastname & email are also used there)
      dispatch(updateInput({ group: InputGroupKeys.PERSONAL_DATA, key: PersonalDataFieldKeys.FIRSTNAME, value: firstName }))
      dispatch(updateInput({ group: InputGroupKeys.PERSONAL_DATA, key: PersonalDataFieldKeys.LASTNAME, value: lastName }))
      dispatch(updateInput({ group: InputGroupKeys.PERSONAL_DATA, key: PersonalDataFieldKeys.EMAIL, value: email }))

      // Create a newsletter prospect
      await axios.post('/api/prospects/warm', {
        email,
        firstName,
        language,
        lastName,
        flow: WarmProspectFlow.NEWSLETTER
      })

      trackEvent({ category: 'subscription', object: 'newsletter', action: 'submit' })
      setSubmissionState('success')
    } catch (e) {
      setSubmissionState('failed')
    }
  }

  return (
    <FormProvider {...hookForm}>
      <Form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <FirstName hiddenLabel />

        <LastName hiddenLabel />

        <Email
          grid="full"
          hiddenLabel
          suffix={{
            as: (
              <Button type="submit" size="small" loading={submissionState === 'loading'} disabled={submissionState === 'success'}>
                {t(`common:subscribe.${submissionState && ['success', 'failed'].includes(submissionState) ? submissionState : 'submit'}`)}
              </Button>
            )
          }}
        />

        <small className={classNames('grid-col-full')}>
          <Trans
            t={t}
            i18nKey="subscribe.consent"
            ns="common"
            components={[<Link key="0" href={routes(language).privacy} variant="inline" />]}
          />
        </small>
      </Form>
    </FormProvider>
  )
}

export default SubscribeForm
